import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Phone, CheckCircle, Shield, Clock, Award, HelpCircle, ChevronDown } from 'lucide-react';
import MembershipEnrollmentWizard from '../components/MembershipEnrollmentWizard';
import FloridaPainPointsSection from '../components/FloridaPainPointsSection';
import MemberVsNonMemberTable from '../components/MemberVsNonMemberTable';
import PlanComparisonTable from '../components/PlanComparisonTable';
import DrainPanTreatmentSection from '../components/DrainPanTreatmentSection';
import TermsModal from '../components/TermsModal';
import { initMembershipTracking, trackPageView } from '../lib/membership-tracking';
import BrandLogo from '@/components/BrandLogo';
import {
  detectBrandFromHost,
  getBrandInfo,
  getPlanComparisonDataForBrand,
  getPlansForBrand,
  BrandKey,
} from '@/lib/brand-utils';
import { Plan } from '@/lib/plans/climate-data';

type BrandConfig = ReturnType<typeof getBrandInfo> & {
  plans: Plan[];
  benefits?: Array<{ title: string; description: string; icon: React.ReactNode }>;
};

export default function MembershipLandingPageV2() {
  const navigate = useNavigate();
  const brandKey: BrandKey = detectBrandFromHost();
  const brandInfo = useMemo(() => getBrandInfo(brandKey), [brandKey]);
  const brandPlans = useMemo(() => getPlansForBrand(brandKey), [brandKey]);
  const config: BrandConfig = useMemo(() => ({ ...brandInfo, plans: brandPlans }), [brandInfo, brandPlans]);
  const [showTerms, setShowTerms] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0);

  useEffect(() => {
    const pageTitle = `${config.name} Comfort Club`;
    document.title = pageTitle;

    initMembershipTracking();
    trackPageView({
      page_path: window.location.pathname,
      page_location: window.location.href,
      page_title: pageTitle,
      brand: brandKey,
    });
  }, [brandKey, config.name]);

  const handleLogin = () => {
    navigate('/portal');
  };

  const faqs = [
    {
      q: 'How do I schedule my tune-up?',
      a: `Call us at ${config.phoneDisplay} or book online. We recommend scheduling at least 30 days before your renewal date so your maintenance is completed on time.`,
    },
    {
      q: 'Is this a month-to-month membership?',
      a: 'No. This is an annual membership (12-month term). Monthly billing is an installment plan for the annual fee (12 payments), not a month-to-month agreement.',
    },
    {
      q: 'Does my membership automatically renew?',
      a: 'Yes. Memberships renew each year so your protection does not lapse. We will send a reminder 30-60 days before renewal, and you can stop renewal with written notice (full Terms & Conditions are shown during enrollment before you pay).',
    },
    {
      q: 'What if I need service after hours?',
      a: 'Higher-tier members have 24/7/365 emergency HVAC service access when service is available. All members receive priority scheduling and member savings.',
    },
    {
      q: 'What is the drain pan treatment?',
      a: 'Our drain pan treatment prevents the buildup of zoogleal slime (biofilm) that can clog your AC drain line and cause water damage. It\'s included with Premier and Optimum tiers.',
    },
    {
      q: 'Can I cancel my membership?',
      a: 'Memberships are annual contracts. You may request cancellation in writing, but the full annual fee is due. You can choose not to renew at the end of your term with 30-day written notice.',
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <BrandLogo className="h-10 w-auto" />
            <div className="flex items-center gap-4">
              <a href={`tel:${config.phone}`} className="flex items-center gap-2 text-gray-700 hover:text-gray-900 font-semibold">
                <Phone className="w-5 h-5" />
                <span className="hidden sm:inline">{config.phoneDisplay}</span>
              </a>
              <button
                onClick={handleLogin}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Member Login
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section - NO PRICING */}
      <section className="bg-gradient-to-br from-blue-600 to-blue-800 text-white py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              Protect Your Home Comfort Investment
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-blue-100">
              Year-round peace of mind with priority service, maintenance, and exclusive member savings
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="#enrollment-wizard"
                className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold bg-white text-blue-600 rounded-lg hover:bg-blue-50 transition-all shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                Sign Up Now
              </a>
              <a
                href={`tel:${config.phone}`}
                className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold bg-blue-700 text-white rounded-lg hover:bg-blue-600 transition-all border-2 border-white/30"
              >
                <Phone className="w-5 h-5 mr-2" />
                {config.phoneDisplay}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-gray-900">
            Why Join Our Comfort Club?
          </h2>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-gray-900">Protect Your Equipment</h3>
              <p className="text-gray-600">
                Regular maintenance catches issues early and keeps your HVAC system running efficiently for years.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-gray-900">Priority Service</h3>
              <p className="text-gray-600">
                Members get priority scheduling and faster service when you need it most—especially during peak seasons.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Award className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-gray-900">Exclusive Savings</h3>
              <p className="text-gray-600">
                Get member-only discounts on repairs, replacements, and upgrades—plus coverage on select plans.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Florida Pain Points Section */}
      <FloridaPainPointsSection />

      {/* Member vs Non-Member Table */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <MemberVsNonMemberTable />
        </div>
      </section>

      {/* Drain Pan Treatment Section */}
      <DrainPanTreatmentSection />

      {/* Membership Comparison (per Comfort_Club_Membership_Comparison.docx) */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-3xl md:text-4xl font-bold mb-3 text-gray-900">Membership Comparison</h2>
              <p className="text-lg text-gray-700">
                See what’s included in each tier — updated to match our latest Comfort Club comparison sheet.
              </p>
            </div>

            <div className="rounded-2xl border-2 shadow-lg overflow-hidden" style={{ borderColor: config.colors.primary }}>
              <PlanComparisonTable
                plans={config.plans}
                comparisonData={getPlanComparisonDataForBrand(brandKey)}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Phone-first Enrollment (original live-site flow) */}
      <section id="enrollment-wizard" className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900">Start with your phone number</h2>
              <p className="text-lg text-gray-700">
                We’ll look up your account (or create one) and guide you through a secure enrollment.
              </p>
            </div>

            <MembershipEnrollmentWizard initialSystems={1} initialBillingFrequency="monthly" />
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-gray-900">
              Frequently Asked Questions
            </h2>
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div key={index} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                  <button
                    onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                    className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                  >
                    <span className="font-semibold text-gray-900">{faq.q}</span>
                    <ChevronDown
                      className={`w-5 h-5 text-gray-500 transition-transform ${
                        openFaqIndex === index ? 'transform rotate-180' : ''
                      }`}
                    />
                  </button>
                  {openFaqIndex === index && (
                    <div className="px-6 py-4 border-t border-gray-100 bg-gray-50">
                      <p className="text-gray-700">{faq.a}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-gradient-to-br from-blue-600 to-blue-800 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Join Hundreds of Satisfied Members</h2>
          <p className="text-xl mb-8 text-blue-100 max-w-2xl mx-auto">
            Experience the peace of mind that comes with professional HVAC maintenance and priority service.
          </p>
          <a
            href="#enrollment-wizard"
            className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold bg-white text-blue-600 rounded-lg hover:bg-blue-50 transition-all shadow-lg hover:shadow-xl transform hover:scale-105"
          >
            Sign Up Now
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-300 py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <BrandLogo className="h-8 w-auto mx-auto mb-6" />
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-6">
              <a href={`tel:${config.phone}`} className="flex items-center gap-2 hover:text-white transition-colors">
                <Phone className="w-4 h-4" />
                {config.phoneDisplay}
              </a>
              <span className="hidden sm:inline text-gray-600">|</span>
              <button
                onClick={() => setShowTerms(true)}
                className="hover:text-white transition-colors"
              >
                Terms & Conditions
              </button>
            </div>
            <p className="text-sm text-gray-500">
              © {new Date().getFullYear()} {config.name}. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

      {/* Terms Modal */}
      {showTerms && (
        <TermsModal
          isOpen={showTerms}
          onClose={() => setShowTerms(false)}
        />
      )}
    </div>
  );
}
